import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';
import { When, Directions } from 'components/Tables/ScheduleList';
import { bookingReference } from 'helpers/bookingReference';

// Column and card layouts, shared with the page's loading skeleton.
export const MIN_TABLE_WIDTH = 960;

export const COLUMNS = [
  { area: 'ref', label: 'Booking #', width: '110px', bone: 'single' },
  { area: 'when', label: 'Service date', width: '150px', bone: 'short', icon: 'time' },
  { area: 'customer', label: 'Customer', width: 'minmax(200px, 1.4fr)' },
  { area: 'address', label: 'Address', width: 'minmax(220px, 1.6fr)', icon: 'map-marker' },
  { area: 'status', label: 'Status', width: '120px', bone: 'pill' },
  { area: 'actions', label: '', width: '72px', bone: 'none' },
];

export const CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'customer status actions',
    'ref ref ref',
    'when when when',
    'address address address',
  ],
};

const STATUS_TONES = {
  draft: 'grey',
  confirmed: 'green',
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const FILTERS = {
  status: 'status',
  first_name: 'first name',
  last_name: 'last name',
};

@inject('store', 'clientStore', 'bookingStore', 'viewStore') @observer
class BookingTable extends Component {
  @observable isOpenAddEquipmentDialog = false;
  @observable isDeleteDialogVisible = false;
  @observable service;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        bookingSearch
      }
    } = this.props;
    this.props.viewStore.bookingSearch = {
      ...bookingSearch,
      page: newPage
    };
    this.props.viewStore.bookings();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.bookingStore.deleteBooking(this.service)
      .then(this.onSuccessfulDeleteBooking.bind(this))
      .catch(this.onFailDeleteBooking.bind(this))
  }

  onSuccessfulDeleteBooking(response) {
    Toast.show({ icon: 'thumbs-up', message: 'booking deleted successfully!', intent: Intent.SUCCESS })
    this.service = null;
    this.isDeleteDialogVisible = false;
  }

  onFailDeleteBooking(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete booking!', intent: Intent.DANGER })
  }

  handleServiceTypeClick(event, service) {
    switch (event.label) {
      case 'Edit':
        this.service = service;
        this.props.viewStore.bookingShow({uuid: service.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.service = service;
        break;
      default:
    }
  }

  // Keep the date range; drop the search term and every other filter.
  clearFilters() {
    const { viewStore } = this.props;
    const { bookingSearch } = viewStore;
    viewStore.bookingSearch = {
      ...bookingSearch,
      term: '*',
      where: {
        start_date: bookingSearch.where.start_date,
        first_name: null,
        last_name: null,
      },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.bookings();
  }

  renderEmpty() {
    const { viewStore } = this.props;
    const { bookingSearch } = viewStore;
    const filters = DataList.activeFilters(bookingSearch.where, FILTERS);
    const term = bookingSearch.term && bookingSearch.term !== '*' ? bookingSearch.term : null;
    const createBooking = { label: 'Create booking', icon: 'plus', onClick: () => viewStore.newBooking() };
    if (filters.length > 0 || term) {
      return (
        <DataList.Empty
          noun="bookings"
          filters={filters}
          term={term}
          onClearFilters={this.clearFilters.bind(this)}
          extraAction={createBooking}
        />
      );
    }
    return (
      <DataList.Empty
        noun="bookings"
        title="No bookings in this date range"
        message="Requests made through your booking form show up here. Try a wider date range above, or create a booking yourself."
        extraAction={{ ...createBooking, primary: true }}
      />
    );
  }

  render() {
    let {
      viewStore,
      bookingStore: {
        bookings: {
          objects,
          meta
        }
      }
    } = this.props;

    if (objects.length === 0) {
      return this.renderEmpty();
    }
    const pager = (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination
            value={meta.page}
            total={meta.total_pages}
            onChange={this.onPaginationChange.bind(this)}
          />
        </div>
      </Footer>
    );
    return (
      <React.Fragment>
        {pager}
        <DataList.List
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="bookings"
          note={DataList.sortNote(viewStore.bookingSearch.order)}
        >
          {objects.map((booking) => {
            const attributes = booking.attributes;
            const name = [attributes.first_name, attributes.last_name].filter(Boolean).join(' ') || 'No name given';
            const contact = [attributes.email, attributes.phone].filter(Boolean).join(', ');
            // Autocomplete stores a full address ("1 Yonge St, Toronto, ON, Canada"),
            // so the line under it only adds what it doesn't already say.
            const isFullAddress = String(attributes.address || '').split(',').length >= 3;
            const area = (isFullAddress
              ? [attributes.zip && !String(attributes.address).includes(attributes.zip) && attributes.zip]
              : [attributes.city, attributes.state, attributes.zip]
            ).filter(Boolean).join(', ');
            const fullAddress = [attributes.address, area].filter(Boolean).join(', ');
            return (
              <DataList.Row key={booking.id}>
                <DataList.Cell area="ref">
                  <span className="dl-lines__primary dl-lines__primary--plain">{bookingReference(attributes.id)}</span>
                </DataList.Cell>
                <DataList.Cell area="when">
                  <When start={attributes.start_date} />
                </DataList.Cell>
                <DataList.Cell area="customer">
                  <div className="dl-lines">
                    <DataList.RowLink href={`/dashboard/bookings/${attributes.id}/show`} label={`Booking for ${name}`}>
                      {name}
                    </DataList.RowLink>
                    {contact && <div className="dl-muted dl-truncate" title={contact}>{contact}</div>}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="address">
                  {attributes.address ? (
                    <div className="dl-lines">
                      <div className="dl-lines__primary dl-lines__primary--plain" title={attributes.address}>
                        {attributes.address}
                      </div>
                      <div className="dl-muted schedule-client__where">
                        {area && <span className="dl-truncate" title={area}>{area}</span>}
                        <Directions location={{ full_address: fullAddress }} />
                      </div>
                    </div>
                  ) : (
                    <DataList.Muted>No address</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="status">
                  <DataList.Pill tone={STATUS_TONES[attributes.status] || 'grey'}>
                    {capitalise(attributes.status)}
                  </DataList.Pill>
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      {
                        icon: 'edit',
                        label: 'Edit booking',
                        onClick: () => viewStore.bookingShow({ uuid: attributes.id }),
                      },
                      {
                        icon: 'trash',
                        label: 'Delete booking',
                        intent: 'danger',
                        onClick: () => this.handleServiceTypeClick({ label: 'Delete' }, booking),
                      },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>Warnning! Deleting this booking will not be recoverable.</p>
          <p>Are you sure you want to do this?</p>
        </Alert>
        {pager}
      </React.Fragment>
    )
  }

}

export default BookingTable;
